"use client";
import React from "react";
import {
  ApiResponse,
  Sport,
  StartingQuarterbackResponse,
  TeamBasic,
  TeamColor,
  formatEmptyState,
} from "common";
import useFetch from "@/utils/fetch";
import Card from "@/components/primitives/card";
import Icon from "@/components/primitives/icon";
import { LoadingCircle } from "@/components/primitives/loading";
import NotFound from "@/components/primitives/not-found";
import TeamLogo from "@/components/team/logo";
import { truncateName } from "@/utils/injuries";
import Link from "next/link";
import { createPlayerProfileSlug } from "@/utils/url";

export default function StartingQuarterbacks({
  awayTeam,
  homeTeam,
  sport,
  gameId,
  season
}: {
  awayTeam: TeamBasic & TeamColor;
  homeTeam: TeamBasic & TeamColor;
  sport: Sport;
  gameId: number;
  season: number;
}) {
  const request: string = `${process.env.NEXT_PUBLIC_API_URL}/nfl/StartingPlayerStats/${gameId}/${season}`;
  const { data: clientData, isLoading, isError, error } =
    useFetch<StartingQuarterbackResponse | null>(null, request, []);

  const data = clientData;
  return (
    <Card
      header={{
        title: (
          <>
            <Icon name="football" size="md" className="fill-primary-500" />
            <span>Starting Quarterbacks</span>
          </>
        ),
      }}
    >
      <div className="table-container mt-2">
        {isLoading ? (
          <div className="flex justify-center items-center h-[220px]">
            <LoadingCircle />
          </div>
        ) : !isLoading && (!data || isError) ? (
          <NotFound label="Quarterbacks TBD" />
        ) : (
          data && (
            <div className="table-container mt-2 text-sm-medium text-secondary-300">
              <div className="flex flex-row justify-between">
                {data.away ? (
                  <Link
                    href={createPlayerProfileSlug(sport, data.away.id)}
                    className="flex-basis-6/12 flex-row flex items-center gap-4 uppercase text-left"
                  >
                    <div
                      className="w-12 h-12 border rounded-full flex items-center justify-center"
                      style={{ borderColor: `#${awayTeam.primaryColor}` }}
                    >
                      <TeamLogo team={awayTeam} size="sm" link sport={sport} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="text-title-bold">
                        {truncateName(data.away.firstName)} {data.away.lastName}
                      </div>
                      <div className="text-label-lg text-secondary-400">
                        #{data.away.jersey} 
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-center">
                    {formatEmptyState("tbd")}
                  </div>
                )}
                {data.home ? (
                  <Link
                    href={createPlayerProfileSlug(sport, data.home.id)}
                    className="flex-basis-6/12 flex-row flex items-center gap-4 uppercase text-right"
                  >
                    <div
                      className="w-12 h-12 border rounded-full flex items-center justify-center order-2"
                      style={{ borderColor: `#${homeTeam.primaryColor}` }}
                    >
                      <TeamLogo team={homeTeam} size="sm" link sport={sport} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="text-title-bold">
                        {truncateName(data.home.firstName)} {data.home.lastName}
                      </div>
                      <div className="text-label-lg text-secondary-400">
                        #{data.home.jersey}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-center">
                    {formatEmptyState("tbd")}
                  </div>
                )}
              </div>

              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away ? (
                    <>
                      {data.away?.yardsPerGame}
                    </>
                  ) : (
                    formatEmptyState("empty")
                  )}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  PYDS/G
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home ? (
                    <>
                      {data.home?.yardsPerGame}
                    </>
                  ) : (
                    formatEmptyState("empty")
                  )}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.attempts || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  ATT
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.attempts || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.completions || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  COMP
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.completions || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.completionPct || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  COMP%
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.completionPct || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.yardsPerAttempt || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  YDS/ATT
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.yardsPerAttempt || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.touchdowns || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  TD
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.touchdowns || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.interceptions || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  INT
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.interceptions || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.rating || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  QBR
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.rating || formatEmptyState("empty")}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </Card>
  );
}
